import { addDoc, collection, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db, quoteOwnerUserId } from './firebase';

const quoteEmailApiUrl = process.env.REACT_APP_QUOTE_EMAIL_API_URL;

export const defaultConfig = {
  general: {
    enabled: true,
    hourlyRate: 15,
    currency: 'EUR',
    notificationEmail: '',
    quoteValidityDays: 30,
    defaultQuoteNotes: 'This estimate is based on the details submitted through the website quote estimator.',
    privacyBusinessName: '',
    privacyContactEmail: '',
    privacyRetentionPeriod: '',
    privacyJurisdiction: '',
    allowCustomPageNames: true,
    pageSuggestions: ['Home', 'About', 'Services', 'Contact', 'Gallery', 'Blog', 'Shop'],
  },
  pageLengths: [
    { id: 'short', label: 'Short', hours: 2 },
    { id: 'medium', label: 'Medium', hours: 4 },
    { id: 'long', label: 'Long', hours: 6 },
  ],
  stylingOptions: [
    { id: 'full-style-guide', label: 'I have a full styling document', description: 'Brand guidelines and visual rules are ready.', hours: 2 },
    { id: 'colours-fonts-logo', label: 'I have specific colours, fonts and logo', description: 'The core brand direction is known.', hours: 4 },
    { id: 'logo-only', label: 'I only have a logo', description: 'A visual system needs to be developed from the logo.', hours: 6 },
    { id: 'styling-help', label: 'I need styling help', description: 'Brand styling support is needed.', hours: 8 },
  ],
  animationOptions: [
    { id: 'none', label: 'No animations', description: 'Static interface treatment.', hours: 0 },
    { id: 'basic', label: 'Basic animations', description: 'Subtle transitions and reveal effects.', hours: 3 },
    { id: 'advanced', label: 'Advanced animations', description: 'More detailed motion and custom interactions.', hours: 8 },
  ],
  features: [],
};

const emptyBusinessProfile = {
  name: '',
  email: '',
  phone: '',
  address: '',
  vatNumber: '',
};

function makeId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

export function mergeConfig(config = {}) {
  return {
    general: { ...defaultConfig.general, ...(config.general || {}) },
    pageLengths: Array.isArray(config.pageLengths) && config.pageLengths.length ? config.pageLengths : defaultConfig.pageLengths,
    stylingOptions: Array.isArray(config.stylingOptions) && config.stylingOptions.length ? config.stylingOptions : defaultConfig.stylingOptions,
    animationOptions: Array.isArray(config.animationOptions) && config.animationOptions.length ? config.animationOptions : defaultConfig.animationOptions,
    features: Array.isArray(config.features) ? config.features : defaultConfig.features,
  };
}

export async function loadQuoteConfig() {
  const snapshot = await getDoc(doc(db, 'users', quoteOwnerUserId, 'ai_quotes', 'config'));
  return snapshot.exists() ? mergeConfig(snapshot.data()) : defaultConfig;
}

function normalizeBusinessProfile(profile = {}) {
  return {
    ...emptyBusinessProfile,
    name: String(profile.name || ''),
    email: String(profile.email || ''),
    phone: String(profile.phone || ''),
    address: String(profile.address || ''),
    vatNumber: String(profile.vatNumber || ''),
  };
}

export async function loadBusinessProfile() {
  const snapshot = await getDoc(doc(db, 'users', quoteOwnerUserId, 'settings', 'businessProfile'));
  return snapshot.exists() ? normalizeBusinessProfile(snapshot.data()) : emptyBusinessProfile;
}

export function money(amount, currency = 'EUR') {
  return new Intl.NumberFormat('en-MT', {
    style: 'currency',
    currency,
  }).format(Number(amount || 0));
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function uniqueEmails(emails) {
  const seen = new Set();

  return emails
    .map((email) => String(email || '').trim().toLowerCase())
    .filter(isValidEmail)
    .filter((email) => {
      if (seen.has(email)) return false;
      seen.add(email);
      return true;
    });
}

function ownerNotificationRecipients(config) {
  return uniqueEmails(String(config.general.notificationEmail || '')
    .split(/[;,]/)
    .map((email) => email.trim()));
}

function quoteNotificationRecipients({ config, customer }) {
  return uniqueEmails([
    ...ownerNotificationRecipients(config),
    customer?.email,
  ]);
}

export function buildQuoteNotificationEmailPayload({ config, customer, quoteId, estimate, quotePayload }) {
  const recipients = quoteNotificationRecipients({ config, customer });
  const subject = `New website quote request - ${customer.name || 'Unknown customer'}`;
  const lineItems = quotePayload.items.map((line) => (
    `<li><strong>${escapeHtml(line.desc)}</strong>: ${escapeHtml(line.qty)} hours x ${escapeHtml(money(line.unit, quotePayload.currency))}</li>`
  )).join('');
  const body = `
    <h2>New website quote request</h2>
    <p><strong>Customer:</strong> ${escapeHtml(customer.name || 'Unknown customer')}</p>
    <p><strong>Email:</strong> ${escapeHtml(customer.email || 'No email provided')}</p>
    <p><strong>Phone:</strong> ${escapeHtml(customer.phone || 'No phone provided')}</p>
    <p><strong>Quote ID:</strong> ${escapeHtml(quoteId)}</p>
    <p><strong>Estimated hours:</strong> ${escapeHtml(estimate.totalHours)}</p>
    <p><strong>Estimated total:</strong> ${escapeHtml(money(estimate.totals.total, quotePayload.currency))}</p>
    <h3>Line items</h3>
    <ul>${lineItems}</ul>
  `;

  return {
    to: recipients,
    subject,
    body,
    replyTo: customer.email,
  };
}

export function calculateQuote({ config, pages, stylingOptionId, animationOptionId, selectedFeatureIds }) {
  const mergedConfig = mergeConfig(config);
  const hourlyRate = Number(mergedConfig.general.hourlyRate || 0);
  const selectedStyling = mergedConfig.stylingOptions.find((item) => item.id === stylingOptionId);
  const selectedAnimation = mergedConfig.animationOptions.find((item) => item.id === animationOptionId);
  const selectedFeatures = mergedConfig.features
    .filter((feature) => feature.enabled !== false && selectedFeatureIds.includes(feature.id))
    .sort((a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0));

  const pageLines = pages.filter((page) => page.name.trim()).map((page) => {
    const length = mergedConfig.pageLengths.find((item) => item.id === page.lengthId) || mergedConfig.pageLengths[0];
    return {
      id: makeId('line'),
      desc: `${page.name.trim()} Page - ${length.label}`,
      qty: Number(length.hours || 0),
      unit: hourlyRate,
      meta: { type: 'page', name: page.name.trim(), lengthId: length.id, lengthLabel: length.label },
    };
  });

  const optionLines = [
    selectedStyling && {
      id: makeId('line'),
      desc: `Styling - ${selectedStyling.label}`,
      qty: Number(selectedStyling.hours || 0),
      unit: hourlyRate,
      meta: { type: 'styling', id: selectedStyling.id },
    },
    selectedAnimation && {
      id: makeId('line'),
      desc: `Animations - ${selectedAnimation.label}`,
      qty: Number(selectedAnimation.hours || 0),
      unit: hourlyRate,
      meta: { type: 'animation', id: selectedAnimation.id },
    },
  ].filter(Boolean);

  const featureLines = selectedFeatures.map((feature) => ({
    id: makeId('line'),
    desc: `Feature - ${feature.name}`,
    qty: Number(feature.hours || 0),
    unit: hourlyRate,
    meta: { type: 'feature', id: feature.id, category: feature.category || '' },
  }));

  const lines = [...pageLines, ...optionLines, ...featureLines];
  const totalHours = lines.reduce((sum, line) => sum + Number(line.qty || 0), 0);
  const subtotal = lines.reduce((sum, line) => sum + Number(line.qty || 0) * Number(line.unit || 0), 0);

  return {
    lines,
    selectedStyling,
    selectedAnimation,
    selectedFeatures,
    totalHours,
    totals: {
      subtotal,
      vatAmount: 0,
      discount: 0,
      paymentsPaid: 0,
      paymentsPending: 0,
      paid: 0,
      pending: 0,
      balanceDue: subtotal,
      total: subtotal,
    },
  };
}

function dueDateFromDays(days) {
  const date = new Date();
  date.setDate(date.getDate() + Number(days || 30));
  return date.toISOString().slice(0, 10);
}

async function hashText(value) {
  const input = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', input);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

async function customerRefForEmail(email) {
  const normalizedEmail = email.trim().toLowerCase();
  const emailHash = await hashText(normalizedEmail);
  return doc(db, 'users', quoteOwnerUserId, 'customers', `ai_${emailHash}`);
}

async function sendQuoteNotification({ config, customer, quoteId, estimate, quotePayload }) {
  if (!quoteEmailApiUrl) {
    return { status: 'not_configured', error: 'REACT_APP_QUOTE_EMAIL_API_URL is not configured.' };
  }

  const recipients = quoteNotificationRecipients({ config, customer });
  if (recipients.length === 0) {
    return { status: 'missing_notification_email', error: 'No valid owner or customer email is available for the quote notification.' };
  }

  const emailPayload = buildQuoteNotificationEmailPayload({ config, customer, quoteId, estimate, quotePayload });

  try {
    const response = await fetch(quoteEmailApiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(emailPayload),
    });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok || payload.ok === false) {
      return { status: 'failed', error: payload.error || `Email API returned HTTP ${response.status}.` };
    }

    return { status: payload.status || 'sent' };
  } catch (err) {
    return { status: 'failed', error: err.message || 'Email API request failed.' };
  }
}

export async function submitQuoteRequest({ config, pages, stylingOptionId, animationOptionId, selectedFeatureIds, contact }) {
  const estimate = calculateQuote({ config, pages, stylingOptionId, animationOptionId, selectedFeatureIds });
  const businessProfile = await loadBusinessProfile();
  const today = new Date().toISOString().slice(0, 10);
  const normalizedEmail = contact.email.trim().toLowerCase();
  const customerPayload = {
    source: 'ai_quotes',
    name: contact.displayName.trim(),
    email: normalizedEmail,
    phone: contact.phone.trim(),
    address: '',
    vatNumber: '',
    updatedAt: serverTimestamp(),
  };

  const customerRef = await customerRefForEmail(normalizedEmail);
  const existingCustomerSnapshot = await getDoc(customerRef);
  await setDoc(customerRef, {
    ...(existingCustomerSnapshot.exists() ? {} : { createdAt: serverTimestamp() }),
    ...customerPayload,
  }, { merge: true });
  const customer = {
    name: customerPayload.name,
    email: customerPayload.email,
    phone: customerPayload.phone,
    address: '',
    vatNumber: '',
  };
  const quotePayload = {
    source: 'ai_quotes',
    type: 'quote',
    ref: `AIQ-${Date.now().toString().slice(-8)}`,
    date: today,
    dueDate: dueDateFromDays(config.general.quoteValidityDays),
    customerId: customerRef.id,
    customer,
    from: businessProfile,
    currency: config.general.currency || 'EUR',
    status: 'sent',
    paymentLink: '',
    paymentLinkStatus: 'unpaid',
    payments: [],
    lineMode: 'hours',
    items: estimate.lines.map(({ id, desc, qty, unit }) => ({ id, desc, qty, unit })),
    vatPct: 0,
    vatExempt: true,
    discountType: 'percent',
    discountAmt: 0,
    notes: config.general.defaultQuoteNotes || '',
    totals: estimate.totals,
    quoteRequest: {
      pages,
      stylingOption: estimate.selectedStyling || null,
      animationOption: estimate.selectedAnimation || null,
      features: estimate.selectedFeatures,
      totalHours: estimate.totalHours,
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const quoteRef = await addDoc(collection(db, 'users', quoteOwnerUserId, 'ai_quotes'), quotePayload);
  const notificationResult = await sendQuoteNotification({
    config,
    customer,
    quoteId: quoteRef.id,
    estimate,
    quotePayload,
  });
  const submissionPayload = {
    source: 'ai_quotes',
    customer,
    customerId: customerRef.id,
    quoteId: quoteRef.id,
    estimate: {
      totalHours: estimate.totalHours,
      total: estimate.totals.total,
      currency: quotePayload.currency,
      lines: quotePayload.items,
    },
    selectedPages: pages,
    selectedStylingOption: estimate.selectedStyling || null,
    selectedAnimationOption: estimate.selectedAnimation || null,
    selectedFeatures: estimate.selectedFeatures,
    privacyAccepted: true,
    notification: {
      to: quoteNotificationRecipients({ config, customer }),
      apiUrl: quoteEmailApiUrl || '',
      status: notificationResult.status,
      error: notificationResult.error || '',
    },
    createdAt: serverTimestamp(),
  };

  await addDoc(collection(db, 'users', quoteOwnerUserId, 'ai_quotes', 'submissions', 'items'), submissionPayload);

  return {
    customerId: customerRef.id,
    quoteId: quoteRef.id,
    estimate,
  };
}
